import { useEffect, useRef } from "react";
import { useTranslation } from "../context/TranslationContext";
import { getAnalyticsGlossary } from "../content/analyticsGlossary";
import Modal from "./ds/Modal";

const SectionTitle = ({ children }) => <h3 className="mb-space-12 text-caps-label uppercase text-accent-text">{children}</h3>;

const AnalyticsGlossaryModal = ({ isOpen, onClose, highlightSlug, isDark }) => {
  const { t, language } = useTranslation();
  const glossary = getAnalyticsGlossary(language);
  const bodyRef = useRef(null);

  useEffect(() => {
    if (!isOpen || !highlightSlug) return;
    const timer = setTimeout(() => {
      const el = bodyRef.current?.querySelector(`[data-glossary-slug="${highlightSlug}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);
    return () => clearTimeout(timer);
  }, [isOpen, highlightSlug]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={glossary.pageTitle}
      closeLabel={t("common.close")}
      isDark={isDark}
      width="min-[600px]:w-[672px]"
    >
      <div ref={bodyRef} className="flex flex-col gap-space-24">
        <ul className="flex flex-col gap-space-4 text-footnote text-label-secondary">
          {glossary.intro.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>

        {glossary.sections.map((sectionItem) => (
          <section key={sectionItem.title}>
            <SectionTitle>{sectionItem.title}</SectionTitle>
            <div className="flex flex-col gap-space-12">
              {sectionItem.metrics.map((m) => (
                <article
                  key={m.slug || m.title}
                  data-glossary-slug={m.slug || undefined}
                  className={`flex flex-col gap-space-8 rounded-[22px] bg-bg-inset p-space-16 ${
                    highlightSlug && m.slug === highlightSlug ? "shadow-[inset_0_0_0_1.5px_var(--color-accent-default)]" : ""
                  }`}
                >
                  <h4 className="text-subheadline-semibold text-label-primary">{m.title}</h4>
                  <dl className="flex flex-col gap-space-8">
                    {m.blocks.map((block) => (
                      <div key={`${m.title}-${block.label}`} className="flex flex-col gap-[2px]">
                        <dt className="text-caps-label uppercase text-label-secondary">{block.label}</dt>
                        <dd className="text-footnote text-label-primary">{block.text}</dd>
                      </div>
                    ))}
                  </dl>
                </article>
              ))}
            </div>
          </section>
        ))}

        <section>
          <SectionTitle>{glossary.readingTogether.title}</SectionTitle>
          <ul className="flex flex-col gap-space-8">
            {glossary.readingTogether.items.map((item) => (
              <li key={item.title} className="rounded-[22px] bg-bg-inset px-space-16 py-space-12 text-footnote">
                <span className="font-semibold text-label-primary">{item.title}</span>
                <span className="text-label-secondary"> — {item.text}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="flex flex-col gap-[2px] rounded-[22px] bg-message-warning-bg px-space-16 py-space-12">
          <h3 className="text-subheadline-semibold text-message-warning-title">{glossary.important.title}</h3>
          <p className="text-footnote text-label-secondary">{glossary.important.text}</p>
        </section>

        <section>
          <SectionTitle>{glossary.formulas.title}</SectionTitle>
          <ul className="flex flex-col gap-space-4 text-footnote text-label-secondary">
            {glossary.formulas.lines.map((line) => (
              <li key={line} className="font-mono">
                {line}
              </li>
            ))}
          </ul>
        </section>
      </div>
    </Modal>
  );
};

export default AnalyticsGlossaryModal;
